import { useEffect } from "react";

/**
 * Wires up the small bits of interactivity the guide pages need, against
 * content that's injected via dangerouslySetInnerHTML (a big block of
 * static, mostly-inert marketing copy - tables, an accordion via native
 * <details>/<summary>, testimonials - not worth hand-converting to JSX
 * component-by-component, the same call SingleBlog.js already makes for
 * post.content). This attaches plain DOM listeners the same way the
 * source pages' own inline <script> did, just from a React effect instead.
 *
 * - Scroll-reveal: elements with class="reveal" (the fee cards and price
 *   table) start hidden via CSS and fade in once scrolled into view.
 *   Without this they'd stay invisible forever - the CSS has no fallback.
 * - Old/new rules toggle: only present on the lettings guide (#tabOld /
 *   #tabNew buttons switching #rulesOld / #rulesNew visibility). Safe to
 *   always attempt - querySelector just returns null on the sales guide.
 */
export default function useGuideInteractions(containerRef) {
    useEffect(() => {
        const root = containerRef.current;
        if (!root) return undefined;

        const cleanups = [];

        // --- scroll reveal ---
        const revealed = root.querySelectorAll(".reveal");
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if ("IntersectionObserver" in window && !reduceMotion) {
            const io = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            entry.target.classList.add("in");
                            io.unobserve(entry.target);
                        }
                    });
                },
                { threshold: 0.12 }
            );
            revealed.forEach((el) => io.observe(el));
            cleanups.push(() => io.disconnect());
        } else {
            revealed.forEach((el) => el.classList.add("in"));
        }

        // --- old/new rules toggle (lettings guide only) ---
        const tabOld = root.querySelector("#tabOld");
        const tabNew = root.querySelector("#tabNew");
        const rulesOld = root.querySelector("#rulesOld");
        const rulesNew = root.querySelector("#rulesNew");
        if (tabOld && tabNew && rulesOld && rulesNew) {
            const showNew = (yes) => {
                rulesOld.style.display = yes ? "none" : "";
                rulesNew.style.display = yes ? "" : "none";
                tabOld.classList.toggle("active", !yes);
                tabNew.classList.toggle("active", yes);
                tabOld.setAttribute("aria-selected", yes ? "false" : "true");
                tabNew.setAttribute("aria-selected", yes ? "true" : "false");
            };
            const onOld = () => showNew(false);
            const onNew = () => showNew(true);
            tabOld.addEventListener("click", onOld);
            tabNew.addEventListener("click", onNew);
            showNew(false);
            cleanups.push(() => {
                tabOld.removeEventListener("click", onOld);
                tabNew.removeEventListener("click", onNew);
            });
        }

        return () => cleanups.forEach((fn) => fn());
    }, [containerRef]);
}
